import { PropsWithChildren, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { AuthState, restoreAuth } from "store/features/auth";
import { storage } from "utils/storage";
import Axios from "axios";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import routesConfig, { RouteConfig } from "config/routes";
import { CircularProgress, Box } from "@mui/material";

const Authorization: React.FC<PropsWithChildren> = ({ children }) => {
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Restore auth from storage on mount — set Axios header before dispatching
  // so that components mounting after restore already have the token available
  useEffect(() => {
    storage.get("auth").then((value) => {
      if (value) {
        try {
          const parsed = JSON.parse(value);
          if (parsed.token) {
            Axios.defaults.headers.common["Authorization"] =
              `Bearer ${parsed.token}`;
          }
          dispatch(restoreAuth(parsed));
        } catch {
          dispatch(restoreAuth({}));
        }
      } else {
        dispatch(restoreAuth({}));
      }
    });
  }, [dispatch]);

  // Persist auth changes to storage and keep Axios header in sync
  useEffect(() => {
    if (!authInfo.restored) return;

    if (authInfo.token) {
      storage.set(
        "auth",
        JSON.stringify({
          id: authInfo.id,
          token: authInfo.token,
          name: authInfo.name,
          role: authInfo.role,
          nursery: authInfo.nursery,
        }),
      );
      Axios.defaults.headers.common["Authorization"] =
        `Bearer ${authInfo.token}`;
    } else {
      storage.remove("auth");
      delete Axios.defaults.headers.common["Authorization"];
    }
  }, [authInfo]);

  const isAuthorized = useMemo(() => {
    if (!authInfo.restored) return null;

    const matches = matchRoutes(routesConfig, location) || [];
    const { auth = true, role } = matches[0].route as RouteConfig;

    // No auth required for this route (e.g. /login)
    if (!auth) return true;

    // No token — not authorized
    if (!authInfo.token) return false;

    // Route has role restriction — check if user's role is allowed
    if (role && !role.includes(authInfo.role as string)) return false;

    return true;
  }, [location, authInfo]);

  useEffect(() => {
    if (isAuthorized === false) navigate("/login", { replace: true });
  }, [isAuthorized, navigate]);

  if (!authInfo.restored || isAuthorized === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthorized === false) return null;

  return <>{children}</>;
};

export default Authorization;
